import api from './api';

export interface TransportVehicle {
  id: number;
  name: string;
  fleet_number?: string;
  plate_number: string;
  vehicle_type: 'bus' | 'minibus' | 'van' | 'car';
  make_model?: string;
  manufacture_year?: number;
  color?: string;
  capacity: number;
  wheelchair_accessible: boolean;
  gps_device_id?: string;
  safety_inspection_due?: string;
  insurance_expiry?: string;
  is_active: boolean;
}
export interface TransportDriver {
  global_user_id: number;
  first_name: string;
  last_name: string;
  email?: string;
  phone_number?: string;
  branch_id: string;
  branch_name?: string;
  license_number?: string;
  license_expiry?: string;
  emergency_phone?: string;
}
export interface TransportAttendant { global_user_id: number; first_name: string; last_name: string; branch_id: string; branch_name?: string }
export interface TransportRoute { id: number; name: string; direction: 'morning_pickup' | 'afternoon_pickup' | 'afternoon_dropoff' | 'evening_dropoff'; vehicle_id?: number; default_driver_global_user_id?: number; default_driver_name?: string; capacity: number; assigned_seats: number; available_seats: number; start_time: string; weekdays: number[]; is_active: boolean }
export interface TransportCoordinatorTrip { id: string; service_date: string; route_id: number; route_name: string; direction: TransportRoute['direction']; vehicle_name?: string; vehicle_plate_number?: string; status: string; driver_global_user_id?: number; driver_name?: string }
export interface TransportTripManifest { manifest_id: number; student_id: number; student_name: string; route_name: string; stop_name: string; manifest_status: string; exception_note?: string }
export interface TransportReport {
  from_date: string;
  to_date: string;
  trips_scheduled: number;
  trips_completed: number;
  delayed_trips: number;
  active_assignments: number;
  capacity: number;
  manifests: { expected: number; boarded: number; dropped_off: number; handed_off: number; no_show: number; missed_pickup: number; cancelled: number; excused_absence: number };
  fare_totals: { currency: string; billed: number; collected: number; outstanding: number }[];
  routes: { route_id: number; route_name: string; direction: TransportRoute['direction']; capacity: number; assigned_seats: number; manifests: number; completed: number; exceptions: number; delayed_trips: number }[];
}
export interface TransportIncident { id: number; trip_id: string; route_name: string; type: string; details?: string; status: string; created_at: string }
export interface TransportStop { id: number; route_id: number; stop_order: number; name: string; latitude?: number; longitude?: number; scheduled_time?: string }
/** An active trip on the live map. Position fields stay undefined until the vehicle reports its first fix. */
export interface TransportLiveTrip { trip_id: string; route_id: number; route_name: string; direction: TransportRoute['direction']; status: string; driver_name?: string; driver_global_user_id?: number; vehicle_name?: string; vehicle_plate_number?: string; latitude?: number; longitude?: number; speed_kph?: number; captured_at?: string; total_students: number; completed_students: number }
export interface TransportTrailPoint { latitude: number; longitude: number; speed_kph?: number; captured_at: string }
/** A student enrolled on transport, with whether they hold a card. Carries no token material. */
export interface TransportStudentPassRow { assignment_id: number; student_id: number; student_name: string; route_name: string; stop_name: string; direction: TransportRoute['direction']; has_card: boolean; last_used_at?: string }
/** A minted pass. `token` is returned once and never again — the server keeps only its hash. */
export interface TransportIssuedPass { student_id: number; pass_type: 'card' | 'rotating'; token: string; expires_at?: string }
export interface TransportFarePlan { id: number; name: string; route_id: number; stop_id?: number; service_type: TransportRoute['direction']; frequency: 'monthly' | 'term'; amount: number; currency: 'USD' | 'KHR'; fee_schedule_id?: number; is_active: boolean }
export interface TransportPickupContact { id: number; student_id: number; parent_id?: number; name: string; phone?: string; relationship?: string; status: 'pending' | 'approved' | 'revoked'; notes?: string; is_active: boolean }
export interface TransportParentPickupCandidate { parent_id: number; name: string; phone?: string; relationship?: string; can_pickup: boolean }

export const transportApi = {
  async vehicles(): Promise<TransportVehicle[]> {
    const response = await api.get('/transport/vehicles');
    return response.data.data ?? [];
  },
  async createVehicle(payload: Omit<TransportVehicle, 'id'>) {
    return (await api.post('/transport/vehicles', payload)).data.data as TransportVehicle;
  },
  async updateVehicle(vehicleId: number, payload: Omit<TransportVehicle, 'id'>) {
    return (await api.put(`/transport/vehicles/${vehicleId}`, payload)).data.data as TransportVehicle;
  },
  async routes(): Promise<TransportRoute[]> {
    const response = await api.get('/transport/routes');
    return response.data.data ?? [];
  },
  async drivers(): Promise<TransportDriver[]> {
    return (await api.get('/transport/drivers')).data.data ?? [];
  },
  async attendants(): Promise<TransportAttendant[]> {
    return (await api.get('/transport/attendants')).data.data ?? [];
  },
  async updateDriverProfile(driverId: number, payload: Pick<TransportDriver, 'license_number' | 'license_expiry' | 'emergency_phone'>) {
    return (await api.put(`/transport/drivers/${driverId}`, payload)).data.data as TransportDriver;
  },
  async createRoute(payload: Omit<TransportRoute, 'id' | 'assigned_seats' | 'available_seats' | 'default_driver_name'>) {
    return (await api.post('/transport/routes', payload)).data.data as TransportRoute;
  },
  async updateRoute(routeId: number, payload: Omit<TransportRoute, 'id' | 'assigned_seats' | 'available_seats' | 'default_driver_name'>) {
    return (await api.put(`/transport/routes/${routeId}`, payload)).data.data as TransportRoute;
  },
  async setRouteDefaultDriver(routeId: number, driverGlobalUserId?: number) {
    return (await api.put(`/transport/routes/${routeId}/default-driver`, { driver_global_user_id: driverGlobalUserId })).data.data;
  },
  async coordinatorTrips(date: string): Promise<TransportCoordinatorTrip[]> {
    return (await api.get('/transport/trips', { params: { date } })).data.data ?? [];
  },
  async tripManifests(tripId: string): Promise<TransportTripManifest[]> {
    return (await api.get(`/transport/trips/${tripId}/manifests`)).data.data ?? [];
  },
  async report(from: string, to: string): Promise<TransportReport> {
    return (await api.get('/transport/reports', { params: { from, to } })).data.data as TransportReport;
  },
  async openIncidents(): Promise<TransportIncident[]> {
    return (await api.get('/transport/incidents/open')).data.data ?? [];
  },
  async resolveIncident(incidentId: number, resolutionNote: string) {
    return (await api.post(`/transport/incidents/${incidentId}/resolve`, { resolution_note: resolutionNote })).data.data;
  },
  async liveTrips(): Promise<TransportLiveTrip[]> {
    return (await api.get('/transport/trips/live')).data.data ?? [];
  },
  async tripTrail(tripId: string, limit = 500): Promise<TransportTrailPoint[]> {
    return (await api.get(`/transport/trips/${tripId}/trail`, { params: { limit } })).data.data ?? [];
  },
  async transportStudents(): Promise<TransportStudentPassRow[]> {
    return (await api.get('/transport/students')).data.data ?? [];
  },
  /** Mints a single pass. Cards are normally issued by printing an ID card
   *  (see issueStudentPasses); this stays for one-off issuance and for
   *  rotating passes. */
  async issueStudentPass(studentId: number, passType: 'card' | 'rotating' = 'card'): Promise<TransportIssuedPass> {
    return (await api.post(`/transport/students/${studentId}/pass`, { pass_type: passType })).data.data;
  },
  /** Mints card tokens for a batch of students. Returns { studentId: token } —
   *  students not on transport are omitted, and each call replaces any live card. */
  async issueStudentPasses(studentIds: number[]): Promise<Record<number, string>> {
    return (await api.post('/transport/students/passes', { student_ids: studentIds })).data.data ?? {};
  },
  async revokeStudentPass(studentId: number, passType: 'card' | 'rotating' = 'card') {
    return (await api.delete(`/transport/students/${studentId}/pass`, { params: { pass_type: passType } })).data;
  },
  async materializeTrips(serviceDate: string) {
    return (await api.post('/transport/trips/materialize', { service_date: serviceDate })).data.data as { created_manifests: number };
  },
  async assignTripDriver(tripId: string, driverGlobalUserId: number) {
    return (await api.post(`/transport/trips/${tripId}/driver`, { driver_global_user_id: driverGlobalUserId })).data.data;
  },
  async tripAttendants(tripId: string): Promise<number[]> {
    return (await api.get(`/transport/trips/${tripId}/attendants`)).data.data ?? [];
  },
  async setTripAttendants(tripId: string, attendantGlobalUserIds: number[]) {
    return (await api.put(`/transport/trips/${tripId}/attendants`, { attendant_global_user_ids: attendantGlobalUserIds })).data.data;
  },
  async createStop(payload: { route_id: number; stop_order: number; name: string; latitude?: number; longitude?: number; scheduled_time?: string }) {
    return (await api.post('/transport/stops', payload)).data.data;
  },
  async updateStop(stopId: number, payload: { route_id: number; stop_order: number; name: string; latitude?: number; longitude?: number; scheduled_time?: string }) {
    return (await api.put(`/transport/stops/${stopId}`, payload)).data.data as TransportStop;
  },
  async stops(routeId: number): Promise<TransportStop[]> {
    return (await api.get(`/transport/routes/${routeId}/stops`)).data.data ?? [];
  },
  async fares(routeId?: number): Promise<TransportFarePlan[]> {
    const query = routeId ? `?route_id=${routeId}` : '';
    return (await api.get(`/transport/fares${query}`)).data.data ?? [];
  },
  async createFare(payload: Omit<TransportFarePlan, 'id'>) {
    return (await api.post('/transport/fares', payload)).data.data as TransportFarePlan;
  },
  async setFareActive(fareId: number, isActive: boolean) {
    return (await api.put(`/transport/fares/${fareId}`, { is_active: isActive })).data.data;
  },
  async assignStudent(payload: { student_id: number; route_id: number; stop_id: number; fare_plan_id?: number; academic_year_id?: number }) {
    return (await api.post('/transport/assignments', payload)).data.data as { assignment_id: number };
  },
  async endAssignment(assignmentId: number, endedReason: string) {
    return (await api.post(`/transport/assignments/${assignmentId}/end`, { ended_reason: endedReason })).data.data;
  },
  async pickupContacts(studentId: number): Promise<TransportPickupContact[]> {
    return (await api.get(`/transport/students/${studentId}/pickup-contacts`)).data.data ?? [];
  },
  async pickupParentCandidates(studentId: number): Promise<TransportParentPickupCandidate[]> {
    return (await api.get(`/transport/students/${studentId}/pickup-parent-candidates`)).data.data ?? [];
  },
  async createPickupContact(studentId: number, payload: Omit<TransportPickupContact, 'id' | 'student_id'>) {
    return (await api.post(`/transport/students/${studentId}/pickup-contacts`, payload)).data.data as TransportPickupContact;
  },
  async updatePickupContact(studentId: number, contactId: number, payload: Omit<TransportPickupContact, 'id' | 'student_id'>) {
    return (await api.put(`/transport/students/${studentId}/pickup-contacts/${contactId}`, payload)).data.data as TransportPickupContact;
  },
};

export default transportApi;
